import {
    type ReactNode,
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IToggleGroupProps from "./type";
import type {
    ToggleGroupContextType,
    ToggleGroupItemEntries,
    ToggleGroupItemEntry,
    ToggleGroupValue
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import ToggleGroupItem from "./components/item";
import {
    ToggleGroupContext
} from "./context";
import {
    getToggleGroupNavigationIndex,
    getToggleGroupOrderedValues,
    getToggleGroupTabStopValue,
    getToggleGroupNextValue,
    getToggleGroupKeyStep,
    getToggleGroupValues,
    getToggleGroupItems
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useStableCallback
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    AttachedGroupItemContextType
} from "../attachedGroup/type";
import {
    flattenAttachedGroupChildren,
    getAttachedGroupOuterRadius,
    getAttachedGroupPosition
} from "../attachedGroup/stylesheet";
import {
    AttachedGroupItemContext
} from "../attachedGroup/context";
import {
    getControlMeasures
} from "../../helpers/theme/control";
import {
    warnOnce
} from "../../utils/development";

const ToggleGroupItemProvider: FC<AttachedGroupItemContextType & {
    children: ReactNode;
}> = ({
    isShadowOwner,
    toggleVariant,
    groupVariant,
    innerRadius,
    orientation,
    outerRadius,
    isAttached,
    isDisabled,
    isOverlap,
    children,
    position,
    size,
    type
}) => {
    const value = useMemo(() => ({
        toggleVariant,
        isShadowOwner,
        groupVariant,
        orientation,
        outerRadius,
        innerRadius,
        isDisabled,
        isAttached,
        isOverlap,
        position,
        size,
        type
    }), [
        toggleVariant,
        groupVariant,
        isShadowOwner,
        orientation,
        isDisabled,
        isAttached,
        outerRadius,
        innerRadius,
        isOverlap,
        position,
        size,
        type
    ]);

    return <AttachedGroupItemContext.Provider
        value={value}
    >
        {children}
    </AttachedGroupItemContext.Provider>;
};

const ToggleGroup: FC<IToggleGroupProps> = ({
    attachBehaviour = "separate",
    spreadBehaviour = "baseline",
    isPressScaleEnabled = true,
    orientation = "horizontal",
    selectBehaviour = "single",
    isClearEnabled = true,
    isDisabled = false,
    variant = "ghost",
    value: valueProp,
    size: sizeProp,
    onValueChange,
    defaultValue,
    initialValue,
    customTheme,
    children,
    onChange,
    style,
    title,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        iconSizes,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const size = sizeProp ?? (pointerType === "coarse" ? "large" : "medium");

    const entriesRef = useRef<ToggleGroupItemEntries>(new Map());

    const [
        valueState,
        setValueState
    ] = useState<ToggleGroupValue | undefined>(defaultValue ?? initialValue);

    const [
        lastFocused,
        setLastFocused
    ] = useState<string | undefined>(undefined);

    const isControlled = valueProp !== undefined;

    const values = useMemo(() => getToggleGroupValues({
        value: isControlled ? valueProp : valueState
    }), [
        isControlled,
        valueState,
        valueProp
    ]);

    const items = getToggleGroupItems({
        children
    });

    const isAttached = attachBehaviour === "attached";

    const currentMeasures = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const outerRadius = getAttachedGroupOuterRadius({
        minHeight: currentMeasures.minHeight,
        orientation,
        radiuses
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        userStyle: style,
        attachBehaviour,
        spreadBehaviour,
        shapeConfigs,
        outerRadius,
        shadowAlpha,
        elevations,
        variant,
        colors,
        spaces
    });

    useEffect(() => {
        if(!isAttached || variant !== "ghost") {
            return;
        }

        warnOnce({
            message: "ToggleGroup: \"attached\" has no edge to share with the \"ghost\" variant, so the items are drawn as \"outline\"."
        });
    }, [
        isAttached,
        variant
    ]);

    useEffect(() => {
        if(!isControlled || onChange || onValueChange) {
            return;
        }

        warnOnce({
            message: "ToggleGroup: a controlled \"value\" needs an \"onValueChange\" handler that updates it."
        });
    }, [
        onValueChange,
        isControlled,
        onChange
    ]);

    useEffect(() => {
        if(initialValue === undefined) {
            return;
        }

        warnOnce({
            message: "ToggleGroup: \"initialValue\" is deprecated and will be removed in the next major version. Use \"defaultValue\" instead."
        });
    }, [
        initialValue
    ]);

    useEffect(() => {
        if(onChange === undefined) {
            return;
        }

        warnOnce({
            message: "ToggleGroup: \"onChange\" is deprecated and will be removed in the next major version. Use \"onValueChange\" instead."
        });
    }, [
        onChange
    ]);

    const registerItem = useCallback((itemValue: string, entry: ToggleGroupItemEntry) => {
        entriesRef.current.set(itemValue, entry);

        return () => {
            if(entriesRef.current.get(itemValue) === entry) {
                entriesRef.current.delete(itemValue);
            }
        };
    }, []);

    const handleToggle = useStableCallback((itemValue: string) => {
        if(isDisabled) {
            return;
        }

        const nextValue = getToggleGroupNextValue({
            value: itemValue,
            selectBehaviour,
            isClearEnabled,
            values
        });

        if(nextValue === undefined) {
            return;
        }

        if(!isControlled) {
            setValueState(nextValue);
        }

        onValueChange?.(nextValue);

        onChange?.(nextValue);
    });

    const handleNavigate = useStableCallback<Parameters<ToggleGroupContextType["handleNavigate"]>, ReturnType<ToggleGroupContextType["handleNavigate"]>>(({
        value: itemValue,
        key
    }) => {
        const orderedValues = getToggleGroupOrderedValues({
            entries: entriesRef.current,
            items
        });

        const nextValue = orderedValues[getToggleGroupNavigationIndex({
            current: orderedValues.indexOf(itemValue),
            step: getToggleGroupKeyStep({
                orientation,
                direction,
                key
            }),
            count: orderedValues.length,
            key
        })];

        if(nextValue === undefined) {
            return false;
        }

        entriesRef.current.get(nextValue)?.ref.current?.focus();

        return true;
    });

    const tabStopValue = getToggleGroupTabStopValue({
        lastFocused,
        isDisabled,
        values,
        items
    });

    const isStretched = spreadBehaviour === "stretch" && orientation !== "vertical";

    const context = useMemo<ToggleGroupContextType>(() => ({
        handleFocus: setLastFocused,
        isPressScaleEnabled,
        handleNavigate,
        handleToggle,
        registerItem,
        tabStopValue,
        customTheme,
        isStretched,
        values
    }), [
        isPressScaleEnabled,
        handleNavigate,
        tabStopValue,
        handleToggle,
        registerItem,
        isStretched,
        customTheme,
        values
    ]);

    const elements = flattenAttachedGroupChildren({
        children
    });

    const webProps = Platform.OS === "web" ? {
        "aria-orientation": orientation
    } : {};

    return <ToggleGroupContext.Provider
        value={context}
    >
        <View
            {...props}
            {...webProps}
            style={[
                style,
                stylesheet.container,
                orientation === "vertical" ? stylesheet.vertical : stylesheet.horizontal,
                isAttached && stylesheet.attached,
                containerDynamicStyle
            ]}
            accessibilityLabel={title}
            aria-label={title}
            role="group"
        >
            {
                elements.map((element, index) => <ToggleGroupItemProvider
                    position={getAttachedGroupPosition({
                        count: elements.length,
                        index
                    })}
                    innerRadius={radiuses.none}
                    isShadowOwner={isAttached}
                    key={element.key ?? index}
                    outerRadius={outerRadius}
                    orientation={orientation}
                    toggleVariant={variant}
                    isAttached={isAttached}
                    isDisabled={isDisabled}
                    isOverlap={isAttached}
                    size={size}
                >
                    {element}
                </ToggleGroupItemProvider>)
            }
        </View>
    </ToggleGroupContext.Provider>;
};
export default Object.assign(ToggleGroup, {
    Item: ToggleGroupItem
});
